import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { registerCliente, loginCliente } from "../../services/LoginUserCliente";
import { getStoreHeaderBranding } from "../../services/public/Branding";
import {
  getPasswordChecks,
  getPasswordStrength,
  isStrongPasswordValid,
  STRONG_PASSWORD_ERROR,
} from "../../utils/passwordRules";

const EyeIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EyeOffIcon = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
    <line x1="1" y1="1" x2="23" y2="23" />
  </svg>
);

// Input de contraseña con botón para mostrar/ocultar (tema del login del cliente).
function PasswordInputCliente({ name, placeholder, value, onChange }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        className="login-input"
        name={name}
        type={show ? "text" : "password"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required
        style={{ paddingRight: 46 }}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        title={show ? "Ocultar contraseña" : "Mostrar contraseña"}
        tabIndex={-1}
        style={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
          background: "none",
          border: "none",
          cursor: "pointer",
          color: "#6d5efc",
          display: "flex",
          alignItems: "center",
          padding: 0,
        }}
      >
        {show ? EyeOffIcon : EyeIcon}
      </button>
    </div>
  );
}

const initialForm = {
  nombre: "",
  apellido: "",
  correo: "",
  password: "",
  confirmPassword: "",
};

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState(() => {
    return location.state?.mode === "login" ? "login" : "register";
  });

  const [loading, setLoading] = useState(false);
  const [uiMessage, setUiMessage] = useState({ type: "", text: "" });
  const [form, setForm] = useState(initialForm);
  const [tiendaNombre, setTiendaNombre] = useState("");

  useEffect(() => {
    let active = true;
    getStoreHeaderBranding()
      .then((payload) => {
        if (!active) return;
        const b = payload?.data || payload || {};
        setTiendaNombre(b?.nombre_tienda || b?.nombre_comercial || b?.nombre || "");
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const changeMode = (nextMode) => {
    if (loading) return;
    setMode(nextMode);
    setUiMessage({ type: "", text: "" });
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;

    setUiMessage({ type: "", text: "" });

    // Validación de contraseña en el registro (fuerte + confirmación).
    if (mode === "register") {
      if (!isStrongPasswordValid(form.password)) {
        setUiMessage({ type: "error", text: STRONG_PASSWORD_ERROR });
        return;
      }
      if (form.password !== form.confirmPassword) {
        setUiMessage({ type: "error", text: "Las contraseñas no coinciden." });
        return;
      }
    }

    setLoading(true);

    try {
      let response;

      if (mode === "register") {
        response = await registerCliente(form);
      } else {
        response = await loginCliente(form.correo, form.password);
      }

      if (!response?.ok) {
        throw new Error(response?.message || "No se pudo completar la operación.");
      }

      const redirectTo = location.state?.redirectTo || "/cliente";
      navigate(redirectTo, { replace: true });
    } catch (error) {
      const backendErrors = error?.response?.data?.errors;
      const msg =
        Array.isArray(backendErrors) && backendErrors.length > 0
          ? backendErrors[0]?.message || backendErrors[0]?.msg
          : error?.response?.data?.message ||
            error?.message ||
            "No pudimos conectarnos con el servidor.";

      setUiMessage({ type: "error", text: msg });
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStore = () => {
    navigate("/store/inicio");
  };

  const pwChecks = getPasswordChecks(form.password || "");
  const pwStrength = getPasswordStrength(form.password || "");
  const showPw = mode === "register" && (form.password || "").length > 0;
  const pwRules = [
    ["8+ caracteres", pwChecks.length],
    ["Mayúscula", pwChecks.upper],
    ["Minúscula", pwChecks.lower],
    ["Número", pwChecks.number],
    ["Símbolo", pwChecks.symbol],
  ];

  return (
    <div className="login-page">
      <div className="login-wrapper">
        <div className="login-brand-side">

          <h1 className="login-brand-title">
            {mode === "register"
              ? `Crea tu cuenta${tiendaNombre ? ` en ${tiendaNombre}` : ""}`
              : `Accede a tu cuenta${tiendaNombre ? ` en ${tiendaNombre}` : ""}`}
          </h1>

          <p className="login-brand-text">
            Inicia sesión para ver tus pedidos, consultar tus compras y administrar tu cuenta.
          </p>

          <div className="login-brand-mini-cards">
            <div className="login-mini-card">
              <span className="login-mini-card-dot" />
              Consulta tus pedidos
            </div>
            <div className="login-mini-card">
              <span className="login-mini-card-dot" />
              Revisa el estado de compra
            </div>
            <div className="login-mini-card">
              <span className="login-mini-card-dot" />
              Administra tu cuenta
            </div>
          </div>
        </div>

        <div className="login-card">
          <div className="login-card-top">
            <div>
              <h2 className="login-title">
                {mode === "register" ? "Crear cuenta" : "Iniciar sesión"}
              </h2>
              <p className="login-subtitle">
                {mode === "register"
                  ? "Completa tus datos para registrarte"
                  : "Ingresa tus credenciales para continuar"}
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={handleBackToStore}
            disabled={loading}
            className={`login-secondary-button ${loading ? "is-loading" : ""}`}
          >
            Volver a la tienda
          </button>

          {uiMessage.text && (
            <p
              style={{
                margin: "16px 0 4px",
                fontSize: 13.5,
                fontWeight: 600,
                lineHeight: 1.5,
                textAlign: "left",
                color: uiMessage.type === "error" ? "#dc2626" : "#3156c9",
              }}
            >
              {uiMessage.text}
            </p>
          )}

          <form key={mode} onSubmit={handleSubmit} className="login-form-wrapper">
            {mode === "register" && (
              <div className="login-row">
                <input
                  className="login-input"
                  name="nombre"
                  placeholder="Nombre"
                  value={form.nombre}
                  onChange={handleChange}
                  required
                />

                <input
                  className="login-input"
                  name="apellido"
                  placeholder="Apellido"
                  value={form.apellido}
                  onChange={handleChange}
                  required
                />
              </div>
            )}

            <input
              className="login-input"
              name="correo"
              placeholder="Correo electrónico"
              type="email"
              value={form.correo}
              onChange={handleChange}
              required
            />

            <PasswordInputCliente
              name="password"
              placeholder="Contraseña"
              value={form.password}
              onChange={handleChange}
            />

            {showPw && (
              <div style={{ marginTop: -4 }}>
                <div style={{ display: "flex", gap: 5, marginBottom: 6 }}>
                  {[1, 2, 3].map((seg) => (
                    <div
                      key={seg}
                      style={{
                        flex: 1,
                        height: 5,
                        borderRadius: 4,
                        background: pwStrength.level >= seg ? pwStrength.color : "#e5e7eb",
                      }}
                    />
                  ))}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: pwStrength.color, marginBottom: 6 }}>
                  Seguridad: {pwStrength.label}
                </div>
                <ul
                  style={{
                    listStyle: "none",
                    padding: 0,
                    margin: 0,
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "4px 10px",
                  }}
                >
                  {pwRules.map(([texto, ok]) => (
                    <li key={texto} style={{ fontSize: 12, color: ok ? "#16a34a" : "#9ca3af", display: "flex", gap: 6 }}>
                      <span style={{ fontWeight: 800 }}>{ok ? "✓" : "○"}</span>
                      {texto}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {mode === "register" && (
              <PasswordInputCliente
                name="confirmPassword"
                placeholder="Confirmar contraseña"
                value={form.confirmPassword}
                onChange={handleChange}
              />
            )}

            {mode === "login" && (
              <div style={{ textAlign: "right", marginTop: -4 }}>
                <span
                  className="login-link"
                  role="button"
                  tabIndex={0}
                  style={{ fontSize: 13 }}
                  onClick={() => navigate("/cliente/recuperar-contrasena")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") navigate("/cliente/recuperar-contrasena");
                  }}
                >
                  ¿Olvidaste tu contraseña?
                </span>
              </div>
            )}

            <button
              className={`login-button ${loading ? "is-loading" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <span className="login-loading-content">
                  <span className="login-spinner" />
                  Procesando...
                </span>
              ) : mode === "register" ? (
                "Crear cuenta"
              ) : (
                "Entrar"
              )}
            </button>
          </form>

          <div className="login-switch">
            {mode === "register" ? (
              <>
                ¿Ya tienes una cuenta?{" "}
                <span
                  className="login-link"
                  role="button"
                  tabIndex={0}
                  onClick={() => changeMode("login")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") changeMode("login");
                  }}
                >
                  Inicia sesión
                </span>
              </>
            ) : (
              <>
                ¿No tienes una cuenta?{" "}
                <span
                  className="login-link"
                  role="button"
                  tabIndex={0}
                  onClick={() => changeMode("register")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") changeMode("register");
                  }}
                >
                  Crea una
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="login-footer">cyravi by Nexus Bridge</div>

      <style>
        {`
          :root {
            --login-bg: #f8faff;
            --login-white: #ffffff;
            --login-text: #182033;
            --login-text-soft: #5f6b85;
            --login-text-muted: #7e89a4;
            --login-border: rgba(95, 108, 133, 0.14);
            --login-border-strong: rgba(99, 102, 241, 0.20);
            --login-primary: #6d5efc;
            --login-primary-dark: #4f46e5;
            --login-secondary: #4da3ff;
            --login-input-bg: #fbfcff;
            --login-shadow: 0 20px 60px rgba(74, 85, 120, 0.12);
            --login-error-bg: #fff1f2;
            --login-error-border: #fecdd3;
            --login-error-text: #be123c;
            --login-info-bg: #eef4ff;
            --login-info-border: #c7d7fe;
            --login-info-text: #3156c9;
          }

          @keyframes loginCardIn {
            from {
              opacity: 0;
              transform: translateY(14px) scale(0.98);
            }
            to {
              opacity: 1;
              transform: translateY(0) scale(1);
            }
          }

          @keyframes loginFadeSlide {
            from {
              opacity: 0;
              transform: translateX(18px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }

          @keyframes loginSpin {
            to {
              transform: rotate(360deg);
            }
          }

          * {
            box-sizing: border-box;
          }

          .login-page {
            min-height: 100dvh;
            min-height: 100svh;
            width: 100%;
            max-width: 100%;
            background: var(--login-bg);
            position: relative;
            overflow-x: hidden;
            overflow-y: auto;
            /* column: un solo hijo en flujo (wrapper) ocupa todo el ancho; row lo dejaba mal en móvil */
            display: flex;
            flex-direction: column;
            align-items: stretch;
            justify-content: center;
            padding:
              max(32px, env(safe-area-inset-top, 0px))
              max(20px, env(safe-area-inset-right, 0px))
              max(72px, env(safe-area-inset-bottom, 0px))
              max(20px, env(safe-area-inset-left, 0px));
            font-family: 'Inter', 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
          }

          .login-wrapper {
            width: 100%;
            max-width: min(1120px, 100%);
            margin-left: auto;
            margin-right: auto;
            flex: 0 1 auto;
            min-width: 0;
            display: grid;
            /* Mobile-first: una columna; escritorio en @media (min-width: 1025px) */
            grid-template-columns: 1fr;
            gap: clamp(16px, 3vw, 28px);
            align-items: center;
            position: relative;
            z-index: 1;
          }

          .login-brand-side {
            padding: 0;
            min-width: 0;
            text-align: center;
            display: flex;
            flex-direction: column;
            align-items: center;
          }

          .login-brand-badge {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            padding: 10px 16px;
            border-radius: 999px;
            background: linear-gradient(135deg, #f4efff, #eef5ff);
            color: var(--login-primary-dark);
            font-size: 12px;
            font-weight: 800;
            letter-spacing: 0.12em;
            border: 1px solid var(--login-border-strong);
            box-shadow: 0 8px 24px rgba(109, 94, 252, 0.08);
          }

          .login-brand-title {
            margin: 18px 0 12px;
            font-size: clamp(2rem, 5vw, 3.4rem);
            line-height: 1.05;
            font-weight: 900;
            color: var(--login-text);
            letter-spacing: -0.04em;
            max-width: min(520px, 100%);
          }

          .login-brand-text {
            margin: 0;
            max-width: min(520px, 100%);
            font-size: 1rem;
            line-height: 1.7;
            color: var(--login-text-soft);
          }

          .login-brand-mini-cards {
            display: flex;
            flex-wrap: wrap;
            gap: 12px;
            margin-top: 28px;
            justify-content: center;
          }

          .login-mini-card {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            background: rgba(255,255,255,0.8);
            border: 1px solid var(--login-border);
            color: var(--login-text);
            padding: 12px 16px;
            border-radius: 16px;
            box-shadow: 0 12px 30px rgba(74, 85, 120, 0.08);
            font-weight: 700;
            font-size: 14px;
            min-width: 0;
          }

          .login-mini-card-dot {
            width: 10px;
            height: 10px;
            border-radius: 50%;
            background: linear-gradient(135deg, #6d5efc, #4da3ff);
            box-shadow: 0 0 0 4px rgba(109, 94, 252, 0.10);
            flex-shrink: 0;
          }

          .login-card {
            width: 100%;
            max-width: min(460px, 100%);
            justify-self: center;
            background: rgba(255,255,255,0.88);
            backdrop-filter: blur(14px);
            border: 1px solid var(--login-border);
            border-radius: 28px;
            padding: 30px 28px;
            box-shadow: var(--login-shadow);
            animation: loginCardIn 0.55s ease;
          }

          .login-card-top {
            display: flex;
            align-items: flex-start;
            justify-content: space-between;
            gap: 12px;
            margin-bottom: 8px;
          }

          .login-title {
            margin: 0;
            font-size: clamp(1.6rem, 3vw, 1.9rem);
            line-height: 1.1;
            font-weight: 900;
            color: var(--login-text);
            letter-spacing: -0.03em;
          }

          .login-subtitle {
            margin-top: 8px;
            margin-bottom: 0;
            font-size: 14px;
            line-height: 1.55;
            color: var(--login-text-muted);
          }

          .login-secondary-button {
            width: 100%;
            padding: 13px 16px;
            border-radius: 16px;
            border: 1px solid var(--login-border);
            background: linear-gradient(135deg, #ffffff, #f7faff);
            color: var(--login-text);
            font-weight: 800;
            cursor: pointer;
            box-shadow: 0 10px 24px rgba(74, 85, 120, 0.06);
            transition: all 0.2s ease;
            margin-top: 18px;
          }

          .login-message-box {
            margin-top: 16px;
            margin-bottom: 4px;
            padding: 13px 14px;
            border-radius: 16px;
            font-weight: 700;
            font-size: 13px;
            line-height: 1.5;
            border: 1px solid transparent;
            word-break: break-word;
          }

          .login-message-box.error {
            background: var(--login-error-bg);
            border-color: var(--login-error-border);
            color: var(--login-error-text);
          }

          .login-message-box.info {
            background: var(--login-info-bg);
            border-color: var(--login-info-border);
            color: var(--login-info-text);
          }

          .login-form-wrapper {
            margin-top: 18px;
            display: flex;
            flex-direction: column;
            gap: 14px;
            animation: loginFadeSlide 0.35s ease;
          }

          .login-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 14px;
          }

          .login-input {
            width: 100%;
            min-width: 0;
            padding: 15px 16px;
            border-radius: 16px;
            border: 1px solid var(--login-border);
            background: var(--login-input-bg);
            color: var(--login-text);
            outline: none;
            font-size: 14px;
            font-weight: 500;
            box-shadow: inset 0 1px 2px rgba(0,0,0,0.02);
            transition: border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
          }

          .login-input:focus {
            border-color: rgba(79, 70, 229, 0.35);
            box-shadow: 0 0 0 4px rgba(109, 94, 252, 0.10);
          }

          .login-button {
            width: 100%;
            padding: 15px 18px;
            border-radius: 18px;
            border: none;
            cursor: pointer;
            background: linear-gradient(135deg, #6d5efc, #4da3ff);
            color: #ffffff;
            font-weight: 900;
            font-size: 14px;
            letter-spacing: 0.02em;
            margin-top: 6px;
            box-shadow: 0 18px 34px rgba(92, 104, 255, 0.22);
            transition: transform 0.2s ease, opacity 0.2s ease;
          }

          .login-button:hover,
          .login-secondary-button:hover,
          .login-link:hover {
            opacity: 0.95;
          }

          .login-button:active,
          .login-secondary-button:active {
            transform: scale(0.99);
          }

          .is-loading {
            opacity: 0.88;
            cursor: default;
          }

          .login-loading-content {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 10px;
          }

          .login-spinner {
            width: 16px;
            height: 16px;
            border: 2px solid rgba(255,255,255,0.35);
            border-top-color: #ffffff;
            border-radius: 50%;
            animation: loginSpin 0.85s linear infinite;
          }

          .login-switch {
            margin-top: 20px;
            text-align: center;
            font-size: 14px;
            color: var(--login-text-soft);
          }

          .login-link {
            color: var(--login-primary-dark);
            font-weight: 900;
            cursor: pointer;
          }

          .login-footer {
            position: absolute;
            left: 50%;
            bottom: max(20px, env(safe-area-inset-bottom, 0px));
            transform: translateX(-50%);
            font-size: 13px;
            font-weight: 800;
            color: #6f7a94;
            letter-spacing: 0.08em;
            z-index: 1;
            text-align: center;
            padding: 0 max(16px, env(safe-area-inset-left, 0px)) 0 max(16px, env(safe-area-inset-right, 0px));
            width: 100%;
            max-width: 100%;
            box-sizing: border-box;
          }

          @media (prefers-reduced-motion: reduce) {
            .login-card,
            .login-form-wrapper {
              animation: none;
            }
            .login-spinner {
              animation: none;
              border-top-color: rgba(255,255,255,0.85);
            }
          }

          /* Dos columnas solo en pantallas anchas (evita layout roto en tablet/móvil) */
          @media (min-width: 1025px) {
            .login-wrapper {
              grid-template-columns: minmax(0, 1fr) minmax(300px, 460px);
              gap: 28px;
            }

            .login-brand-side {
              padding: 10px 10px 10px 6px;
              text-align: left;
              align-items: flex-start;
            }

            .login-brand-mini-cards {
              justify-content: flex-start;
            }

            .login-card {
              justify-self: end;
            }
          }

          @media (max-width: 1024px) {
            .login-page {
              padding:
                max(28px, env(safe-area-inset-top, 0px))
                max(18px, env(safe-area-inset-right, 0px))
                max(70px, env(safe-area-inset-bottom, 0px))
                max(18px, env(safe-area-inset-left, 0px));
            }

            .login-wrapper {
              gap: clamp(18px, 4vw, 24px);
            }
          }

          @media (max-width: 768px) {
            .login-page {
              padding:
                max(20px, env(safe-area-inset-top, 0px))
                max(16px, env(safe-area-inset-right, 0px))
                max(68px, env(safe-area-inset-bottom, 0px))
                max(16px, env(safe-area-inset-left, 0px));
            }

            .login-wrapper {
              max-width: min(520px, 100%);
              gap: 18px;
            }

            .login-brand-side {
              text-align: center;
              align-items: center;
            }

            .login-brand-title {
              font-size: clamp(1.9rem, 8vw, 2.7rem);
              max-width: 100%;
            }

            .login-brand-text {
              font-size: clamp(14px, 3.8vw, 15px);
              line-height: 1.65;
            }

            .login-brand-mini-cards {
              width: 100%;
            }

            .login-mini-card {
              flex: 1 1 calc(50% - 6px);
              min-width: min(100%, 200px);
              justify-content: flex-start;
            }

            .login-card {
              padding: clamp(20px, 4vw, 24px) clamp(16px, 4vw, 20px);
              border-radius: 24px;
            }

            .login-row {
              grid-template-columns: 1fr;
            }

            /* Evita zoom automático al enfocar en iOS Safari */
            .login-input {
              font-size: 16px;
            }
          }

          @media (max-width: 560px) {
            .login-page {
              padding:
                max(16px, env(safe-area-inset-top, 0px))
                max(12px, env(safe-area-inset-right, 0px))
                max(62px, env(safe-area-inset-bottom, 0px))
                max(12px, env(safe-area-inset-left, 0px));
            }

            .login-wrapper {
              max-width: 100%;
              gap: 16px;
            }

            .login-brand-side {
              text-align: center;
              align-items: center;
            }

            .login-brand-badge {
              font-size: 11px;
              padding: 9px 14px;
            }

            .login-brand-title {
              margin-top: 14px;
              margin-bottom: 10px;
              font-size: clamp(1.7rem, 8vw, 2.2rem);
            }

            .login-brand-text {
              font-size: 14px;
              line-height: 1.6;
            }

            .login-brand-mini-cards {
              flex-direction: column;
              align-items: stretch;
              gap: 10px;
              width: 100%;
            }

            .login-mini-card {
              width: 100%;
              flex: none;
              min-width: 0;
              font-size: 13px;
              padding: 11px 14px;
              border-radius: 14px;
            }

            .login-card {
              max-width: 100%;
              width: 100%;
              padding: 20px 16px;
              border-radius: 20px;
            }

            .login-title {
              font-size: clamp(22px, 5.5vw, 24px);
            }

            .login-subtitle,
            .login-switch {
              font-size: 13px;
            }

            .login-input,
            .login-button,
            .login-secondary-button {
              min-height: 48px;
              font-size: 16px;
            }

            .login-footer {
              position: static;
              transform: none;
              margin-top: 16px;
              bottom: auto;
            }
          }

          @media (max-width: 380px) {
            .login-page {
              padding:
                max(12px, env(safe-area-inset-top, 0px))
                max(10px, env(safe-area-inset-right, 0px))
                max(20px, env(safe-area-inset-bottom, 0px))
                max(10px, env(safe-area-inset-left, 0px));
            }

            .login-card {
              padding: 18px 14px;
              border-radius: 18px;
            }

            .login-brand-title {
              font-size: 1.55rem;
              line-height: 1.12;
            }

            .login-brand-text {
              font-size: 13.5px;
            }

            .login-title {
              font-size: 22px;
            }

            .login-input,
            .login-button,
            .login-secondary-button {
              padding: 14px;
              min-height: 46px;
            }

            .login-message-box {
              font-size: 12px;
            }
          }

          /* Tablets / móvil en horizontal: menos altura útil */
          @media (max-height: 640px) {
            .login-page {
              align-items: flex-start;
              padding-top: max(16px, env(safe-area-inset-top, 0px));
            }

            .login-brand-title {
              margin-top: 8px;
              margin-bottom: 8px;
              font-size: clamp(1.5rem, 5vh, 2.4rem);
            }

            .login-brand-text {
              line-height: 1.5;
            }

            .login-brand-mini-cards {
              margin-top: 14px;
              gap: 8px;
            }

            .login-mini-card {
              padding: 8px 12px;
              font-size: 12px;
            }
          }

          @media (max-height: 720px) and (min-width: 1025px) {
            .login-page {
              align-items: flex-start;
              padding-top: max(24px, env(safe-area-inset-top, 0px));
              padding-bottom: max(72px, env(safe-area-inset-bottom, 0px));
            }
          }
        `}
      </style>
    </div>
  );
}